function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }
function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }
import React, { useEffect, useMemo, useState } from 'react';
import { func, shape, string } from 'prop-types';
import { CachePersistor } from 'apollo-cache-persist';
import { ApolloProvider, createHttpLink } from '@apollo/client';
import { ApolloClient } from '@apollo/client/core';
import { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { BrowserPersistence } from '@magento/peregrine/lib/util';
import attachClient from '@magento/peregrine/lib/Apollo/attachClientToStore';
import typePolicies from '@magento/peregrine/lib/Apollo/policies';
import { CACHE_PERSIST_PREFIX } from '@magento/peregrine/lib/Apollo/constants';
import StoreCodeRoute from '../components/StoreCodeRoute';
import { shrinkGETQuery } from '../util/shrinkGETQuery';
import { getStoreCode } from "../components/StoreCodeRoute/storeCodeRoute";

/**
 * To improve initial load time, create an apollo cache object as soon as
 * this module is executed, since it doesn't depend on any component props.
 * The tradeoff is that we may be creating an instance we don't end up needing.
 */
var preInstantiatedCache = new InMemoryCache({
  typePolicies,
  // POSSIBLE_TYPES is injected into the bundle by webpack at build time.
  possibleTypes: POSSIBLE_TYPES
});
var storage = new BrowserPersistence();

/**
 * The counterpart to `@magento/venia-drivers` is an adapter that provides
 * context objects to the driver dependencies. The default implementation in
 * `@magento/venia-drivers` uses modules such as `react-redux`, which
 * have implicit external dependencies. This adapter provides all of them at
 * once.
 *
 * Consumers of Venia components can either implement a similar adapter and
 * wrap their Venia component trees with it, or they can override `src/drivers`
 * so its components don't depend on context and IO.
 *
 * @param {String} props.apiBase base path for url
 * @param {Object} props.apollo.cache an apollo cache instance
 * @param {Object} props.apollo.client an apollo client instance
 * @param {Object} props.apollo.link an apollo link instance
 * @param {Object} props.store redux store to provide
 */
var VeniaAdapter = props => {
  var {
    apiBase,
    apollo = {},
    children,
    store
  } = props;
  var [initialized, setInitialized] = useState(false);
  var apolloClient = useMemo(() => {
    var cache = apollo.cache || preInstantiatedCache;
    var link = apollo.link || VeniaAdapter.apolloLink(apiBase);
    var client = apollo.client || new ApolloClient({
      cache,
      link
    });
    var storeCode = getStoreCode();
    var persistor = new CachePersistor({
      key: "".concat(CACHE_PERSIST_PREFIX, "-").concat(storeCode),
      cache,
      storage: window.localStorage,
      debug: process.env.NODE_ENV === 'development'
    });
    return Object.assign(client, {
      apiBase,
      persistor
    });
  }, [apiBase, apollo]);

  // perform blocking async work here
  useEffect(() => {
    if (initialized) return;

    // immediately invoke this async function
    _asyncToGenerator(function* () {
      // restore persisted data to the Apollo cache
      yield apolloClient.persistor.restore();

      // attach the Apollo client to the Redux store
      attachClient(apolloClient);

      // mark this routine as complete
      setInitialized(true);
    })();
  }, [apolloClient, initialized]);
  if (!initialized) {
    // TODO: Replace with app skeleton. See PWA-547.
    return null;
  }
  var storeCodeRouteHandler = null;
  var browserRouterProps = {};
  if (process.env.USE_STORE_CODE_IN_URL === 'true') {
    var storeCode = getStoreCode();
    browserRouterProps.basename = "/".concat(storeCode);

    // Include the store code route handler that manages and updates the
    // stored code based on the url.
    storeCodeRouteHandler = /*#__PURE__*/React.createElement(StoreCodeRoute, null);
  }
  return /*#__PURE__*/React.createElement(ApolloProvider, {
    client: apolloClient
  }, /*#__PURE__*/React.createElement(ReduxProvider, {
    store: store
  }, /*#__PURE__*/React.createElement(BrowserRouter, browserRouterProps, storeCodeRouteHandler, children)));
};

// Create a new store link to include store codes and currency in the request
VeniaAdapter.storeLink = setContext((_, _ref2) => {
  var {
    headers
  } = _ref2;
  var storeCurrency = storage.getItem('store_view_currency') || 'CAD';
  var storeCode = getStoreCode();

  // return the headers to the context so httpLink can read them
  return {
    headers: _objectSpread({
      'store': storeCode,
      'Content-Currency': storeCurrency
    }, headers)
  };
});

/**
 * We attach this Link as a static method on VeniaAdapter because
 * other modules in the codebase need access to it.
 */
VeniaAdapter.apolloLink = apiBase => {
  // Intercept and shrink URLs from GET queries. Using
  // GET makes it possible to use edge caching in Magento
  // Cloud, but risks exceeding URL limits with default usage
  // of Apollo's http link. `shrinkGETQuery` encodes the URL
  // in a more efficient way.
  var customFetchToShrinkQuery = (uri, options) => {
    var url = uri;
    if (options.method === 'GET') {
      url = shrinkGETQuery(uri);
    }
    return fetch(url, options);
  };
  return createHttpLink({
    uri: apiBase,
    fetch: customFetchToShrinkQuery,
    // Warning: useGETForQueries risks exceeding URL length limits. These limits
    // in practice are typically set at or behind where TLS terminates. For Magento
    // Cloud and Fastly, 8kb is the maximum by default
    // https://docs.fastly.com/en/guides/resource-limits#request-and-response-limits
    useGETForQueries: true
  });
};
VeniaAdapter.propTypes = {
  apiBase: string.isRequired,
  apollo: shape({
    client: shape({
      query: func.isRequired
    }),
    link: shape({
      request: func.isRequired
    }),
    cache: shape({
      readQuery: func.isRequired
    })
  }),
  store: shape({
    dispatch: func.isRequired,
    getState: func.isRequired,
    subscribe: func.isRequired
  }).isRequired
};
export default VeniaAdapter;